import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import Modal from '../../components/common/Modal';
import { useAuth } from '../../contexts/AuthContext';

export default function LoanProducts() {
  const [products, setProducts] = useState([]);
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const { hasPermission } = useAuth();

  const [form, setForm] = useState({
    productCode: '', name: '', minAmount: '', maxAmount: '',
    interestRate: '', interestType: 'tiered', interestCalculationMethod: 'daily',
    durationMin: '', durationMax: '', durationUnit: 'days', repaymentFrequency: 'monthly',
    processingFeePercentage: '0', insuranceFeePercentage: '0',
    latePenaltyTier1Percent: '5', latePenaltyTier2Percent: '15', latePenaltyTier1MaxDays: '15',
    latePaymentGraceDays: '0',
    allowEarlySettlement: true, earlySettlementPenaltyPercent: '0'
  });

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = () => api.get('/loan-products').then(res => setProducts(res.data.data));

  const openEdit = (p) => {
    setEditId(p._id);
    setForm({
      productCode: p.productCode, name: p.name, minAmount: p.minAmount, maxAmount: p.maxAmount,
      interestRate: p.interestRate, interestType: p.interestType || 'tiered',
      interestCalculationMethod: p.interestCalculationMethod || 'daily',
      durationMin: p.durationMin, durationMax: p.durationMax,
      durationUnit: p.durationUnit, repaymentFrequency: p.repaymentFrequency,
      processingFeePercentage: p.processingFeePercentage || 0,
      insuranceFeePercentage: p.insuranceFeePercentage || 0,
      latePenaltyTier1Percent: p.latePenaltyTier1Percent ?? 5,
      latePenaltyTier2Percent: p.latePenaltyTier2Percent ?? 15,
      latePenaltyTier1MaxDays: p.latePenaltyTier1MaxDays ?? 15,
      latePaymentGraceDays: p.latePaymentGraceDays ?? 0,
      allowEarlySettlement: p.allowEarlySettlement !== false,
      earlySettlementPenaltyPercent: p.earlySettlementPenaltyPercent || 0
    });
    setModal(true);
  };

  const resetForm = () => {
    setEditId(null);
    setForm({
      productCode: '', name: '', minAmount: '', maxAmount: '',
      interestRate: '', interestType: 'tiered', interestCalculationMethod: 'daily',
      durationMin: '', durationMax: '', durationUnit: 'days', repaymentFrequency: 'monthly',
      processingFeePercentage: '0', insuranceFeePercentage: '0',
      latePenaltyTier1Percent: '5', latePenaltyTier2Percent: '15', latePenaltyTier1MaxDays: '15',
      latePaymentGraceDays: '0',
      allowEarlySettlement: true, earlySettlementPenaltyPercent: '0'
    });
    setModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      minAmount: parseFloat(form.minAmount),
      maxAmount: parseFloat(form.maxAmount),
      interestRate: parseFloat(form.interestRate),
      processingFeePercentage: parseFloat(form.processingFeePercentage),
      insuranceFeePercentage: parseFloat(form.insuranceFeePercentage),
      latePenaltyTier1Percent: parseFloat(form.latePenaltyTier1Percent),
      latePenaltyTier2Percent: parseFloat(form.latePenaltyTier2Percent),
      latePenaltyTier1MaxDays: parseInt(form.latePenaltyTier1MaxDays),
      latePaymentGraceDays: parseInt(form.latePaymentGraceDays),
      earlySettlementPenaltyPercent: parseFloat(form.earlySettlementPenaltyPercent),
      durationMin: parseInt(form.durationMin),
      durationMax: parseInt(form.durationMax)
    };

    try {
      if (editId) await api.put(`/loan-products/${editId}`, payload);
      else await api.post('/loan-products', payload);
      resetForm();
      fetchProducts();
    } catch (err) {
      const validationErrors = err.response?.data?.errors;
      if (validationErrors) {
        const msg = validationErrors.map(e => `${e.field}: ${e.message}`).join('\n');
        alert(`Validation Failed:\n${msg}`);
      } else {
        alert(err.response?.data?.message || 'Error saving product');
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('⚠️ Are you sure? Deleting a product will affect new loan applications but NOT existing active loans.')) {
      await api.delete(`/loan-products/${id}`);
      fetchProducts();
    }
  };

  const getInterestTypeBadge = (type) => {
    const map = {
      'tiered': { label: '📊 Tiered (Daily)', color: '#7c3aed', bg: '#f5f3ff', border: '#8b5cf6' },
      'flat': { label: '📏 Flat', color: '#2563eb', bg: '#eff6ff', border: '#3b82f6' },
      'reducing-balance': { label: '📉 Reducing', color: '#059669', bg: '#ecfdf5', border: '#10b981' },
      'simple': { label: '🔢 Simple', color: '#d97706', bg: '#fffbeb', border: '#f59e0b' },
    };
    const s = map[type] || map['tiered'];
    return (
      <span style={{
        fontSize: 11, padding: '3px 10px', borderRadius: 12,
        fontWeight: 600, color: s.color, background: s.bg, border: `1px solid ${s.border}`,
      }}>
        {s.label}
      </span>
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <h3>Loan Products</h3>
        {hasPermission('loan-products', 'create') && (
          <button className="btn btn-primary" onClick={() => { resetForm(); setModal(true); }}>+ Add Product</button>
        )}
      </div>

      {/* Info Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
        border: '1px solid #bfdbfe', borderRadius: 10, padding: 16, marginBottom: 16,
      }}>
        <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#1e40af' }}>📊 Dynamic Interest Based on Actual Days Used</h4>
        <div style={{ fontSize: 13, color: '#3b82f6', lineHeight: 1.7 }}>
          <strong>Tiered Interest:</strong> 14% base rate (Days 1–14), rate = day number for Days 15–30 (Day 15 = 15%, Day 20 = 20%), capped at 30%.<br/>
          <strong>Interest continues past maturity</strong> until the loan is fully repaid (capped at 30%).<br/>
          <strong>Early Repayment:</strong> Clients only pay interest for the actual days the loan was active.<br/>
          <strong>Late Penalties:</strong> 5% of outstanding balance (up to 15 days overdue), 15% (more than 15 days overdue).
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Interest Type</th>
                <th>Calc Method</th>
                <th>Amount Range</th>
                <th>Duration</th>
                <th>Late Penalty Tiers</th>
                <th>Early Settlement</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr><td colSpan="9" className="text-center p-4 text-muted">No loan products found. Create one above.</td></tr>
              ) : products.map(p => (
                <tr key={p._id}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{p.productCode}</td>
                  <td className="font-bold">{p.name}</td>
                  <td>{getInterestTypeBadge(p.interestType)}</td>
                  <td>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, background: p.interestCalculationMethod === 'daily' ? '#ecfdf5' : '#f1f5f9', color: p.interestCalculationMethod === 'daily' ? '#059669' : '#64748b', fontWeight: 600 }}>
                      {p.interestCalculationMethod?.toUpperCase() || 'DAILY'}
                    </span>
                  </td>
                  <td>{formatCurrency(p.minAmount)} - {formatCurrency(p.maxAmount)}</td>
                  <td>{p.durationMin}-{p.durationMax} {p.durationUnit}</td>
                  <td>
                    <div style={{ fontSize: 12 }}>
                      <span style={{ color: '#d97706', fontWeight: 600 }}>
                        {p.latePenaltyTier1Percent ?? 5}% ≤ {p.latePenaltyTier1MaxDays ?? 15}d overdue
                      </span>
                      <br/>
                      <span style={{ color: '#dc2626', fontWeight: 600 }}>
                        {p.latePenaltyTier2Percent ?? 15}% &gt; {p.latePenaltyTier1MaxDays ?? 15}d overdue
                      </span>
                      {(p.latePaymentGraceDays > 0) && (
                        <>
                          <br/>
                          <span style={{ color: '#64748b' }}>
                            {p.latePaymentGraceDays}d grace
                          </span>
                        </>
                      )}
                    </div>
                  </td>
                  <td>
                    {p.allowEarlySettlement !== false ? (
                      <span style={{ fontSize: 11, color: '#059669', fontWeight: 600 }}>✅ Allowed</span>
                    ) : (
                      <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600 }}>❌ Not Allowed</span>
                    )}
                  </td>
                  <td>
                    <div className="flex gap-1">
                      {hasPermission('loan-products', 'update') && <button className="btn btn-sm btn-outline" onClick={() => openEdit(p)}>Edit</button>}
                      {hasPermission('loan-products', 'delete') && <button className="btn btn-sm btn-danger" onClick={() => handleDelete(p._id)}>Del</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={modal} onClose={resetForm} title={editId ? "Edit Loan Product" : "Add Loan Product"} size="lg">
        <form onSubmit={handleSubmit}>
          {/* Section 1: Basic Info */}
          <h5 className="mb-2 mt-1 text-primary border-bottom">Basic Information</h5>
          <div className="grid grid-2 gap-1">
            <div className="form-group">
              <label>Product Code*</label>
              <input className="form-control" required value={form.productCode} onChange={e => setForm({...form, productCode: e.target.value})} disabled={!!editId} placeholder="e.g. DAILY10" />
            </div>
            <div className="form-group">
              <label>Product Name*</label>
              <input className="form-control" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. Daily Tiered Loan" />
            </div>
          </div>

          {/* Section 2: Amount & Duration */}
          <h5 className="mb-2 mt-3 text-primary border-bottom">Amount & Duration</h5>
          <div className="grid grid-2 gap-1">
            <div className="form-group"><label>Min Amount (KES)*</label><input type="number" className="form-control" required value={form.minAmount} onChange={e => setForm({...form, minAmount: e.target.value})} /></div>
            <div className="form-group"><label>Max Amount (KES)*</label><input type="number" className="form-control" required value={form.maxAmount} onChange={e => setForm({...form, maxAmount: e.target.value})} /></div>
          </div>
          <div className="grid grid-3 gap-1">
            <div className="form-group"><label>Min Duration*</label><input type="number" className="form-control" required value={form.durationMin} onChange={e => setForm({...form, durationMin: e.target.value})} /></div>
            <div className="form-group"><label>Max Duration*</label><input type="number" className="form-control" required value={form.durationMax} onChange={e => setForm({...form, durationMax: e.target.value})} /></div>
            <div className="form-group">
              <label>Unit*</label>
              <select className="form-control" value={form.durationUnit} onChange={e => setForm({...form, durationUnit: e.target.value})}>
                <option value="days">Days</option>
                <option value="weeks">Weeks</option>
                <option value="months">Months</option>
                <option value="years">Years</option>
              </select>
            </div>
          </div>

          {/* Section 3: Interest & Calculator Configuration */}
          <h5 className="mb-2 mt-3 text-primary border-bottom">Interest & Calculation Settings</h5>

          {form.interestType === 'tiered' && (
            <div style={{ background: '#f5f3ff', border: '1px solid #8b5cf6', borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 12, color: '#5b21b6' }}>
              <strong>📊 Tiered Interest Rules (Based on Actual Days Used):</strong><br/>
              • Days 1–14: 14% base rate (minimum — even if repaid early)<br/>
              • Days 15–30: Rate = day number (Day 15 = 15%, Day 20 = 20%, Day 30 = 30%)<br/>
              • Days 31+: Capped at 30%<br/>
              • Interest continues accruing past maturity until loan is fully repaid<br/>
              • <strong>Early repayment:</strong> client only pays for actual days the loan was active<br/>
              • <strong>Example:</strong> 20-day loan repaid on Day 16 → 16% interest (not 20%)
            </div>
          )}

          <div className="grid grid-2 gap-1">
            <div className="form-group">
              <label>Base Interest Rate (%)*</label>
              <input type="number" step="0.01" className="form-control" required value={form.interestRate} onChange={e => setForm({...form, interestRate: e.target.value})} />
              {form.interestType === 'tiered' && (
                <p className="text-xs text-muted mt-1">For tiered: this is the base rate (typically 14%)</p>
              )}
            </div>
            <div className="form-group">
              <label>Interest Type*</label>
              <select className="form-control" value={form.interestType} onChange={e => setForm({...form, interestType: e.target.value})}>
                <option value="tiered">📊 Tiered (Daily Accrual) — Recommended</option>
                <option value="flat">📏 Flat Rate</option>
                <option value="reducing-balance">📉 Reducing Balance</option>
                <option value="simple">🔢 Simple Interest</option>
              </select>
            </div>
          </div>
          <div className="grid grid-2 gap-1">
            <div className="form-group">
              <label>Calculation Method*</label>
              <select className="form-control" value={form.interestCalculationMethod} onChange={e => setForm({...form, interestCalculationMethod: e.target.value})}>
                <option value="daily">Daily (Recommended for Tiered)</option>
                <option value="monthly">Monthly</option>
                <option value="annual">Annual</option>
              </select>
            </div>
            <div className="form-group">
              <label>Repayment Frequency*</label>
              <select className="form-control" value={form.repaymentFrequency} onChange={e => setForm({...form, repaymentFrequency: e.target.value})}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="bi-weekly">Bi-Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>

          {/* Section 4: Fees */}
          <h5 className="mb-2 mt-3 text-primary border-bottom">Fees</h5>
          <div className="grid grid-3 gap-1">
            <div className="form-group">
              <label>Processing Fee (%)</label>
              <input type="number" step="0.01" className="form-control" value={form.processingFeePercentage} onChange={e => setForm({...form, processingFeePercentage: e.target.value})} placeholder="0.00" />
            </div>
            <div className="form-group">
              <label>Insurance Fee (%)</label>
              <input type="number" step="0.01" className="form-control" value={form.insuranceFeePercentage} onChange={e => setForm({...form, insuranceFeePercentage: e.target.value})} placeholder="0.00" />
            </div>
            <div className="form-group">
              <label>Early Settlement Penalty (%)</label>
              <input type="number" step="0.01" className="form-control" value={form.earlySettlementPenaltyPercent} onChange={e => setForm({...form, earlySettlementPenaltyPercent: e.target.value})} placeholder="0.00" />
              <p className="text-xs text-muted mt-1">Extra fee if client repays before maturity (usually 0%)</p>
            </div>
          </div>

          {/* Section 5: Late Payment Penalties — NEW */}
          <h5 className="mb-2 mt-3 text-primary border-bottom">Late Payment Penalties</h5>

          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 12, color: '#991b1b' }}>
            <strong>⚠️ Penalty Rules:</strong><br/>
            • Penalties apply after the maturity date + grace days<br/>
            • <strong>Tier 1:</strong> Up to <em>N</em> days overdue → Tier 1 % of outstanding balance (principal + interest)<br/>
            • <strong>Tier 2:</strong> More than <em>N</em> days overdue → Tier 2 % of outstanding balance<br/>
            • Penalty is calculated on outstanding principal + accrued interest (no compounding)
          </div>

          <div className="grid grid-3 gap-1">
            <div className="form-group">
              <label>Tier 1 Penalty (%)</label>
              <input type="number" step="0.01" className="form-control" value={form.latePenaltyTier1Percent} onChange={e => setForm({...form, latePenaltyTier1Percent: e.target.value})} placeholder="5" />
              <p className="text-xs text-muted mt-1">Applied for up to N days overdue (default: 5%)</p>
            </div>
            <div className="form-group">
              <label>Tier 2 Penalty (%)</label>
              <input type="number" step="0.01" className="form-control" value={form.latePenaltyTier2Percent} onChange={e => setForm({...form, latePenaltyTier2Percent: e.target.value})} placeholder="15" />
              <p className="text-xs text-muted mt-1">Applied beyond N days overdue (default: 15%)</p>
            </div>
            <div className="form-group">
              <label>Tier 1 Max Days</label>
              <input type="number" className="form-control" value={form.latePenaltyTier1MaxDays} onChange={e => setForm({...form, latePenaltyTier1MaxDays: e.target.value})} placeholder="15" />
              <p className="text-xs text-muted mt-1">Days overdue threshold for Tier 1 → Tier 2 switch (default: 15)</p>
            </div>
          </div>
          <div className="grid grid-2 gap-1">
            <div className="form-group">
              <label>Grace Days Before Penalty</label>
              <input type="number" className="form-control" value={form.latePaymentGraceDays} onChange={e => setForm({...form, latePaymentGraceDays: e.target.value})} placeholder="0" />
              <p className="text-xs text-muted mt-1">Days after maturity before penalties start (default: 0)</p>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 20 }}>
              <input type="checkbox" checked={form.allowEarlySettlement} onChange={e => setForm({...form, allowEarlySettlement: e.target.checked})} style={{ width: 18, height: 18 }} />
              <label style={{ margin: 0, fontWeight: 600 }}>Allow Early Settlement (Recommended: Yes)</label>
            </div>
          </div>

          <button type="submit" className="btn btn-primary w-full mt-2">{editId ? 'Update Product' : 'Create Product'}</button>
        </form>
      </Modal>
    </div>
  );
}